import { useCallback, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import { SHOULD_DIRTY_TRUE } from '../../ServiceTestsFormControlConstants';
import {
    AGENT_ALL_FIELD_NAME,
    AGENT_IDS_FIELD_NAME,
    AGENT_MATCHALLTAGS_FIELD_NAME,
    AGENT_TAGS_FIELD_NAME,
    ALL_MODE,
    SELECTED_MODE,
    TAG_AND,
} from '../../ServiceTestsConstants';

/**
 * Mode state (all/selected/tagged) and agent selection handlers.
 *
 * Manages the agent selection mode and provides handlers for adding/removing agents.
 * Uses tagOpRef (passed via orchestrator from the tag matching hook) for tagged mode writes.
 *
 * @state-ownership
 * - Owns: mode (useState), modeRef (useRef)
 * - Reads from RHF: agentsValue (passed as param for ids/tags in handleModeChange)
 * - Writes to RHF: agents.all, agents.ids, agents.tags, agents.matchAllTags via setValue
 * - Params: { fieldName, setValue, getValues, agentsValue, tagOpRef, initialMode }
 */
export function useAgentMode({ setValue, getValues, agentsValue, tagOpRef, previousNotEmptyAgentsIds, initialMode }) {
    const [mode, setMode] = useState(initialMode);
    const modeRef = useRef(mode);
    modeRef.current = mode;

    const agentsValueIds = agentsValue.ids;

    // Switch mode and update each agents sub-field individually.
    // RHF v6 parent-object setValue('agents', {...}) uses getPath() internally which:
    //   - skips empty arrays entirely (no enumerable properties)
    //   - generates element-level paths (agents.ids[0]) for non-empty arrays,
    //     which don't match the field registered as 'agents.ids'
    // Writing to each leaf field directly avoids both issues.
    const handleModeChange = useCallback(
        (newMode) => {
            setMode(newMode);
            if (newMode === ALL_MODE) {
                setValue(AGENT_ALL_FIELD_NAME, true, SHOULD_DIRTY_TRUE);
                setValue(AGENT_IDS_FIELD_NAME, [], SHOULD_DIRTY_TRUE);
                setValue(AGENT_TAGS_FIELD_NAME, [], SHOULD_DIRTY_TRUE);
                setValue(AGENT_MATCHALLTAGS_FIELD_NAME, false, SHOULD_DIRTY_TRUE);
            } else if (newMode === SELECTED_MODE) {
                setValue(AGENT_ALL_FIELD_NAME, false, SHOULD_DIRTY_TRUE);

                // Rollback last selected not empty version
                let ids = agentsValueIds || [];
                if (isEmpty(ids)) {
                    ids = previousNotEmptyAgentsIds || [];
                }
                setValue(AGENT_IDS_FIELD_NAME, ids, SHOULD_DIRTY_TRUE);

                setValue(AGENT_TAGS_FIELD_NAME, [], SHOULD_DIRTY_TRUE);
                setValue(AGENT_MATCHALLTAGS_FIELD_NAME, false, SHOULD_DIRTY_TRUE);
            } else {
                // 'tagged' - preserve current tagOp via ref to avoid stale closure
                setValue(AGENT_ALL_FIELD_NAME, false, SHOULD_DIRTY_TRUE);
                setValue(AGENT_IDS_FIELD_NAME, [], SHOULD_DIRTY_TRUE);
                setValue(AGENT_TAGS_FIELD_NAME, agentsValue.tags || [], SHOULD_DIRTY_TRUE);
                setValue(AGENT_MATCHALLTAGS_FIELD_NAME, tagOpRef.current === TAG_AND, SHOULD_DIRTY_TRUE);
            }
        },
        [setValue, agentsValueIds, previousNotEmptyAgentsIds, agentsValue.tags, tagOpRef]
    );

    // For nPoint Interface = MULTI (Run only when part of a Wi-Fi Group Test),
    // set model as selected, ids and tags as empty, matchAllTags as false
    const handleMutiAdapterTypeChange = useCallback(() => {
        setMode(SELECTED_MODE);
        setValue(AGENT_ALL_FIELD_NAME, false, SHOULD_DIRTY_TRUE);
        setValue(AGENT_IDS_FIELD_NAME, [], SHOULD_DIRTY_TRUE);
        setValue(AGENT_TAGS_FIELD_NAME, [], SHOULD_DIRTY_TRUE);
        setValue(AGENT_MATCHALLTAGS_FIELD_NAME, false, SHOULD_DIRTY_TRUE);
    }, [setValue]);

    // Receives combined IDs (existing + newly added) from ListSelectorDialog
    const handleAddAgents = useCallback(
        (combinedIds) => {
            setValue(AGENT_IDS_FIELD_NAME, combinedIds, SHOULD_DIRTY_TRUE);
        },
        [setValue]
    );

    // Remove a single agent row by its _id
    const handleRemoveOne = useCallback(
        (record) => {
            const currentIds = getValues(AGENT_IDS_FIELD_NAME) || [];
            setValue(
                AGENT_IDS_FIELD_NAME,
                currentIds.filter((id) => id !== record._id),
                SHOULD_DIRTY_TRUE
            );
        },
        [getValues, setValue]
    );

    // Remove agents based on DataTable multi-selection state
    const handleBulkRemove = useCallback(
        (tableSelection) => {
            if (!tableSelection) {
                return;
            }

            const currentIds = getValues(AGENT_IDS_FIELD_NAME) || [];
            const newIds = tableSelection.inverted
                ? currentIds.filter((id) => tableSelection.selection.includes(id))
                : currentIds.filter((id) => !tableSelection.selection.includes(id));
            setValue(AGENT_IDS_FIELD_NAME, newIds, SHOULD_DIRTY_TRUE);
        },
        [getValues, setValue]
    );

    return {
        mode,
        modeRef,
        handleModeChange,
        handleAddAgents,
        handleRemoveOne,
        handleBulkRemove,
        handleMutiAdapterTypeChange,
    };
}
